import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { X } from 'lucide-react';
import { Button } from '../Button';
import * as agenda from '../../services/agendaService';

/**
 * [ 🤒 Acute Calamiteit / Ziek Melden ]: eerst laten zien wat er vervalt,
 * dan pas de komende 48 uur dichtzetten.
 */
export const CalamiteitDialoog: React.FC<{ onSluiten: () => void; onGemeld: () => void }> = ({ onSluiten, onGemeld }) => {
  const { t } = useTranslation('agenda');
  const [omschrijving, setOmschrijving] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['agenda-calamiteit'], queryFn: agenda.getCalamiteitVoorvertoning });
  useEffect(() => {
    const sluit = (e: KeyboardEvent) => e.key === 'Escape' && onSluiten();
    window.addEventListener('keydown', sluit);
    return () => window.removeEventListener('keydown', sluit);
  }, [onSluiten]);

  const meld = useMutation({
    mutationFn: () => agenda.meldCalamiteit(omschrijving.trim() || null),
    onSuccess: (r) => {
      toast.success(
        t('matrix.calamiteit.gemeld', {
          defaultValue: 'Gemeld. {{afspraken}} medewerker-afspraak/afspraken en {{b2b}} overleg(gen) vervallen; betrokkenen zijn geïnformeerd.',
          afspraken: r.medewerker_afspraken,
          b2b: r.b2b_afspraken,
        })
      );
      onGemeld();
    },
    onError: (err) => toast.error((isAxiosError(err) ? err.response?.data?.message : undefined) ?? t('matrix.detail.fout', 'Dat lukte niet')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onSluiten}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('matrix.calamiteit.titel', '🤒 Acute calamiteit / ziek melden')}</h3>
          <button type="button" onClick={onSluiten} className="p-1 rounded hover:bg-gray-100" aria-label={t('plan.sluiten', 'Sluiten')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm text-gray-700">
          {isLoading || !data ? (
            <p className="text-gray-500">{t('matrix.laden', 'Laden…')}</p>
          ) : (
            <>
              <p>
                {t('matrix.calamiteit.uitleg', {
                  defaultValue: 'Je agenda gaat dicht tot {{tot}}. Medewerkers in je pools worden naar de andere beschikbare professionals in dezelfde pool geleid.',
                  tot: format(new Date(data.tot), 'EEEE d MMMM HH:mm', { locale: nl }),
                })}
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>{t('matrix.calamiteit.medewerker', { defaultValue: '{{count}} medewerker-afspraak/afspraken vervallen (medewerkers krijgen bericht)', count: data.medewerker_afspraken })}</li>
                <li>{t('matrix.calamiteit.b2b', { defaultValue: '{{count}} B2B-overleg(gen) vervallen (opdrachtgevers krijgen bericht)', count: data.b2b_afspraken })}</li>
              </ul>
              <label className="block">
                <span className="text-gray-700">{t('matrix.calamiteit.notitie', 'Notitie (alleen voor jou, optioneel)')}</span>
                <input className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" value={omschrijving} maxLength={200} onChange={(e) => setOmschrijving(e.target.value)} />
              </label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <Button variant="outline" onClick={onSluiten}>{t('plan.annuleren', 'Annuleren')}</Button>
          <Button variant="danger" onClick={() => meld.mutate()} loading={meld.isPending} disabled={!data}>
            {t('matrix.calamiteit.bevestig', 'Agenda 48 uur bevriezen')}
          </Button>
        </div>
      </div>
    </div>
  );
};
